import apiClient from "../../helpers/axios";

const base = "/admin/catalog-review";

export const getCatalogReviewBootstrap = async () => {
    const response = await apiClient.get(`${base}/bootstrap`);
    return response.data;
};

export const initializeCatalogReview = async ({ force = false } = {}) => {
    const response = await apiClient.post(`${base}/initialize`, { force });
    return response.data;
};

export const listOldForms = async (params = {}) => {
    const response = await apiClient.get(`${base}/old-forms`, { params });
    return response.data;
};

export const getOldForm = async (oldNumber) => {
    const response = await apiClient.get(`${base}/old-forms/${oldNumber}`);
    return response.data;
};

export const updateOldForm = async (oldNumber, revision, patch) => {
    const response = await apiClient.patch(`${base}/old-forms/${oldNumber}`, {
        revision,
        patch,
    });
    return response.data;
};

export const listCatalogItems = async (params = {}) => {
    const response = await apiClient.get(`${base}/items`, { params });
    return response.data;
};

export const getCatalogItem = async (purchasedNumber) => {
    const response = await apiClient.get(`${base}/items/${purchasedNumber}`);
    return response.data;
};

export const updateCatalogItem = async (purchasedNumber, revision, patch) => {
    const response = await apiClient.patch(`${base}/items/${purchasedNumber}`, {
        revision,
        patch,
    });
    return response.data;
};

export const mergeCatalogDuplicates = async ({ revision, members, note }) => {
    const response = await apiClient.post(`${base}/duplicates/merge`, {
        revision,
        members,
        note,
    });
    return response.data;
};

export const removeCatalogDuplicate = async ({ oldNumber, revision }) => {
    const response = await apiClient.delete(`${base}/duplicates/${oldNumber}`, {
        data: { revision },
    });
    return response.data;
};

export const listPricingProducts = async (params = {}) => {
    const response = await apiClient.get(`${base}/pricing-products`, { params });
    return response.data;
};

export const getPricingProductDetails = async (productId) => {
    const response = await apiClient.get(
        `${base}/pricing-products/${encodeURIComponent(productId)}`
    );
    return response.data;
};

export const getCatalogProductCategories = async () => {
    const response = await apiClient.get(`${base}/products/categories`);
    return response.data;
};

export const getCatalogProductAnalysis = async (categoryId = '') => {
    const response = await apiClient.get(`${base}/products/analysis`, {
        params: categoryId ? { categoryId } : {},
    });
    return response.data;
};

export const analyzeCatalogProductLinks = async ({ categoryId }) => {
    const response = await apiClient.post(`${base}/products/analyze`, { categoryId });
    return response.data;
};

export const previewCatalogProductLinks = async (payload = {}) => {
    const response = await apiClient.post(`${base}/products/preview`, payload);
    return response.data;
};

export const applyCatalogProductLinks = async (mappings) => {
    const response = await apiClient.post(`${base}/products/apply`, { mappings });
    return response.data;
};

export const revertCatalogProductLinks = async (mappings) => {
    const response = await apiClient.post(`${base}/products/revert`, { mappings });
    return response.data;
};

export const createMissingCatalogProducts = async (payload) => {
    const response = await apiClient.post(`${base}/products/create-missing`, payload);
    return response.data;
};

export const previewCatalogExtension = async (payload = {}) => {
    const response = await apiClient.post(`${base}/products/extension/preview`, payload);
    return response.data;
};

export const applyCatalogExtension = async (payload = {}) => {
    const response = await apiClient.post(`${base}/products/extension/apply`, payload);
    return response.data;
};

export const saveCatalogExtensionGeometry = async (payload = {}) => {
    const response = await apiClient.post(`${base}/products/extension/geometry`, payload);
    return response.data;
};

export const estimateCatalogExtension = async (payload = {}) => {
    const response = await apiClient.post(`${base}/products/extension/estimate`, payload);
    return response.data;
};

export const getCatalogExtensionReportUrl = ({
    categoryId,
    fromNumber,
    toNumber,
} = {}) => {
    const params = new URLSearchParams();

    if (categoryId) params.set('categoryId', categoryId);
    if (fromNumber) params.set('fromNumber', fromNumber);
    if (toNumber) params.set('toNumber', toNumber);

    return `${base}/products/extension/report.csv?${params.toString()}`;
};

export const downloadCatalogExtensionReport = async (payload = {}) => {
    const response = await apiClient.get(
        getCatalogExtensionReportUrl(payload),
        { responseType: 'blob' }
    );

    return response.data;
};

export const getBlankPriceCatalogs = async () => {
    const response = await apiClient.get(`${base}/blank-prices`);
    return response.data;
};

export const saveBlankPriceCatalog = async (key, material, revision) => {
    const response = await apiClient.put(
        `${base}/blank-prices/${encodeURIComponent(key || material?.key || material?.name || 'new')}`,
        { material, revision }
    );
    return response.data;
};

export const deleteBlankPriceCatalog = async (key) => {
    const response = await apiClient.delete(
        `${base}/blank-prices/${encodeURIComponent(key)}`
    );
    return response.data;
};

export const previewBlankPriceRecalculation = async (payload = {}) => {
    const response = await apiClient.post(
        `${base}/blank-prices/recalculate/preview`,
        payload
    );
    return response.data;
};

export const applyBlankPriceRecalculation = async (payload = {}) => {
    const response = await apiClient.post(
        `${base}/blank-prices/recalculate/apply`,
        { ...payload, confirm: true }
    );
    return response.data;
};

export const getPricingPolicy = async () => {
    const response = await apiClient.get(`${base}/pricing-policy`);
    return response.data;
};

export const savePricingPolicy = async (policy, revision) => {
    const response = await apiClient.put(`${base}/pricing-policy`, {
        policy,
        revision,
    });
    return response.data;
};

export const calculatePricingPolicyExample = async (payload = {}) => {
    const response = await apiClient.post(
        `${base}/pricing-policy/calculate`,
        payload
    );
    return response.data;
};

export const getPricingPolicyModelProducts = async (payload = {}) => {
    const response = await apiClient.post(
        `${base}/pricing-policy/model-products`,
        payload
    );
    return response.data;
};

export const previewPricingPolicy = async (payload = {}) => {
    const response = await apiClient.post(
        `${base}/pricing-policy/preview`,
        payload
    );
    return response.data;
};

export const getBlankSuppliers = async () => {
    const response = await apiClient.get(`${base}/blank-suppliers`);
    return response.data;
};

export const saveBlankSupplier = async (key, supplier, revision) => {
    const response = await apiClient.put(
        `${base}/blank-suppliers/${encodeURIComponent(key || supplier?.key || supplier?.name || 'new')}`,
        { supplier, revision }
    );
    return response.data;
};

export const deleteBlankSupplier = async (key) => {
    const response = await apiClient.delete(
        `${base}/blank-suppliers/${encodeURIComponent(key)}`
    );
    return response.data;
};

export const previewBlankSupplierImport = async (importData) => {
    const response = await apiClient.post(
        `${base}/blank-suppliers/import/preview`,
        { importData }
    );
    return response.data;
};

export const applyBlankSupplierImport = async ({ importData, previewFingerprint }) => {
    const response = await apiClient.post(
        `${base}/blank-suppliers/import/apply`,
        {
            importData,
            previewFingerprint,
            confirm: true,
        }
    );
    return response.data;
};

export const getBlankSupplierExportUrl = ({ supplierKey = '', materialKey = '' } = {}) => {
    const params = new URLSearchParams();
    if (supplierKey) params.set('supplierKey', supplierKey);
    if (materialKey) params.set('materialKey', materialKey);
    const query = params.toString();
    return `${base}/blank-suppliers/export.json${query ? `?${query}` : ''}`;
};

export const estimateBlankPrice = async (payload = {}) => {
    const response = await apiClient.post(`${base}/blank-prices/estimate`, payload);
    return response.data;
};

export const estimateBlankPricesBatch = async (payload = {}) => {
    const response = await apiClient.post(`${base}/blank-prices/estimate-batch`, payload);
    return response.data;
};

export const downloadBlankSupplierExport = async ({ supplierKey = '', materialKey = '' } = {}) => {
    const params = {};
    if (supplierKey) params.supplierKey = supplierKey;
    if (materialKey) params.materialKey = materialKey;
    const response = await apiClient.get(`${base}/blank-suppliers/export.json`, {
        params,
        responseType: 'blob',
    });
    return response.data;
};
